import { supabase } from "./supabase";

// O IO do CNAExpert (000216) — zero regra.
//
// A ferramenta lê o CATÁLOGO ÚNICO de CNAEs (`cnaes`, a 000189), o mesmo que o
// Radar Simples e a busca de ocupações do MEI leem. Não existe uma segunda
// lista de CNAE no hub desde 05/09, e não é aqui que ela volta a nascer.
//
// Quem decide se pode ler é `cnae_consultar`, que pergunta a `abrir_card` pelo
// card `cnaexpert` — e não pelo `radar-simples`, que lê a MESMA tabela com
// outro público. Este módulo não decide acesso: ele transporta.

/** Um enquadramento alternativo de FPAS para o mesmo CNAE. */
export type Enquadramento = {
  fpas: string;
  /** A descrição da variante, verbatim da origem: "… - agroindústria",
   *  "Pessoa Jurídica", "Pessoal de Transporte no Comércio atacadista…".
   *  São 74 formas distintas — não há enum possível. */
  descricao: string;
};

export type Cnae = {
  codigo: string;
  num_cnae: string;
  descricao: string;
  fpas: string | null;
  /** O RAT em PERCENTUAL (1, 2 ou 3 — e 0 em um único CNAE, serviços
   *  domésticos). Nulo em 2 das 1.333 linhas do catálogo. */
  rat: number | null;
  /** O grau de risco da NR-4 (1 a 4). **Não** tem relação determinística com o
   *  RAT — são normas diferentes, e derivar um do outro produziria número
   *  errado com cara de certo (medido na 000184). */
  grau_risco: number | null;
  /** A PROCEDÊNCIA do FPAS/RAT: "exato", "exato com alternativas",
   *  "inferido da classe /00", "inferido por família CNAE"… A tela mostra,
   *  porque número certo sem procedência ensina a confiar em número velho. */
  criterio: string | null;
  nota: string | null;
  base_legal: string | null;
  alternativos: Enquadramento[];
};

export type Resultado = {
  termo: string;
  total: number;
  teto: number;
  cnaes: Cnae[];
};

export type Falha = { motivo: string };

export function ehFalha(r: unknown): r is Falha {
  return typeof r === "object" && r !== null && "motivo" in r;
}

export const POR_BUSCA = 30;

export async function consultar(termo: string): Promise<Resultado | Falha | null> {
  const { data, error } = await supabase.rpc("cnae_consultar", {
    p_curso: cursoDaQuery(),
    p_termo: termo.trim() || null,
    p_limite: POR_BUSCA,
  });
  if (error) return null;
  const r = data as { ok?: boolean; motivo?: string } & Resultado;
  if (!r?.ok) return { motivo: r?.motivo ?? "falha" };
  return {
    termo: r.termo ?? "",
    total: Number(r.total ?? 0),
    teto: Number(r.teto ?? POR_BUSCA),
    cnaes: (r.cnaes ?? []) as Cnae[],
  };
}

/**
 * O curso pelo qual o card foi aberto. Vem do `?curso=` que `aberturaDoCard`
 * põe no link; hoje o CNAExpert só pertence à Pós DP, então o padrão acerta
 * sempre — e cravar `pos-dp` sem a query seria descobrir a próxima pós pelo
 * aluno recusado.
 */
function cursoDaQuery(): string {
  const q = new URLSearchParams(window.location.search).get("curso");
  return q && q.trim() !== "" ? q : "pos-dp";
}

/**
 * O RAT como texto. `0` é um valor REAL (serviços domésticos) e nulo é "o
 * catálogo não diz" — as duas coisas são diferentes, e mostrar "0%" onde não há
 * dado seria afirmar uma alíquota que ninguém apurou.
 */
export function ratEmTexto(rat: number | null): string | null {
  if (rat === null || rat === undefined) return null;
  return `${String(rat).replace(".", ",")}%`;
}

/**
 * O critério é INFERIDO? Medido no catálogo: 151 dos 1.333 CNAEs têm FPAS/RAT
 * deduzido da classe `/00` ou da família, não lido de tabela. A tela destaca
 * esses — quem vai fechar uma folha tem direito de saber qual número foi lido e
 * qual foi deduzido.
 */
export function ehInferido(criterio: string | null): boolean {
  return typeof criterio === "string" && criterio.startsWith("inferido");
}
